/**
 * Pending Invitations Tab Component.
 * Shows list of pending invitations with loading, empty, and populated states.
 */

import { useTranslation } from 'react-i18next';
import PendingInvitationRow from './PendingInvitationRow';
import type { TeamInvitation } from '../../types/company';

interface PendingInvitationsTabProps {
  invitations: TeamInvitation[];
  onResend: (invitationId: string) => Promise<void>;
  onCancel: (invitationId: string) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Loading skeleton for pending invitations - 3 shimmer rows.
 */
function PendingInvitationsSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="animate-pulse">
        {/* Header row skeleton */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-4 bg-gray-200 rounded flex-1" />
          ))}
        </div>
        {/* Data rows skeleton */}
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="px-4 py-4 border-b border-gray-100 flex gap-4 items-center"
          >
            <div className="h-4 bg-gray-200 rounded flex-1" />
            <div className="h-6 w-16 bg-gray-200 rounded-full" />
            <div className="h-4 bg-gray-200 rounded flex-1" />
            <div className="h-4 bg-gray-200 rounded flex-1" />
            <div className="h-4 bg-gray-200 rounded flex-1" />
            <div className="flex gap-2">
              <div className="h-8 w-20 bg-gray-200 rounded" />
              <div className="h-8 w-20 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state when no pending invitations exist.
 */
function PendingInvitationsEmptyState() {
  const { t } = useTranslation('team');

  return (
    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="ri-mail-send-line text-3xl text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {t('noPendingInvitations', 'No pending invitations')}
      </h3>
      <p className="text-gray-500 text-sm max-w-md mx-auto">
        {t('invitationsWillAppearHere', 'Invitations you send will appear here')}
      </p>
    </div>
  );
}

export default function PendingInvitationsTab({
  invitations,
  onResend,
  onCancel,
  isLoading = false,
}: PendingInvitationsTabProps) {
  const { t } = useTranslation('team');

  // Show loading skeleton
  if (isLoading) {
    return <PendingInvitationsSkeleton />;
  }

  // Show empty state if no invitations
  if (invitations.length === 0) {
    return <PendingInvitationsEmptyState />;
  }

  // Sort invitations by createdAt descending (newest first)
  const sortedInvitations = [...invitations].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('email', 'Email')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('role', 'Role')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('sentDate', 'Sent')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('expires', 'Expires')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('invitedBy', 'Invited By')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('actions', 'Actions')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedInvitations.map((invitation) => (
            <PendingInvitationRow
              key={invitation.id}
              invitation={invitation}
              onResend={onResend}
              onCancel={onCancel}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
